import React, { useMemo, useState } from 'react';
import {
  Box,
  HStack,
  Image,
  Text,
  VStack,
  SimpleGrid,
  AspectRatio,
  Link,
  Icon,
  List,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { MICROSITE_SECTION_EMPTY_HINT } from './micrositeUtils';

function renderInline(text) {
  const src = String(text || '');
  const parts = [];
  // Split by **bold**, _italic_, ++underline++
  const re = /(\*\*[^*]+\*\*|_[^_]+_|\+\+[^+]+\+\+|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) parts.push({ t: 'text', v: src.slice(last, m.index) });
    const token = m[0];
    if (token.startsWith('**')) parts.push({ t: 'b', v: token.slice(2, -2) });
    else if (token.startsWith('_')) parts.push({ t: 'i', v: token.slice(1, -1) });
    else if (token.startsWith('++')) parts.push({ t: 'u', v: token.slice(2, -2) });
    else if (token.startsWith('[')) {
      const mm = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (mm?.[1] && mm?.[2]) parts.push({ t: 'a', v: mm[1], href: mm[2] });
      else parts.push({ t: 'text', v: token });
    }
    last = m.index + token.length;
  }
  if (last < src.length) parts.push({ t: 'text', v: src.slice(last) });

  return parts.map((p, idx) => {
    if (p.t === 'b') return <Text as="span" fontWeight="800" key={idx}>{p.v}</Text>;
    if (p.t === 'i') return <Text as="span" fontStyle="italic" key={idx}>{p.v}</Text>;
    if (p.t === 'u') return <Text as="span" textDecoration="underline" key={idx}>{p.v}</Text>;
    if (p.t === 'a') {
      return (
        <Link
          key={idx}
          href={p.href}
          color="purple.600"
          fontWeight="700"
          target='_blank'
          rel='noopener noreferrer'>
          {p.v} <Icon boxSize={3} ml={1} asChild><FaExternalLinkAlt /></Icon>
        </Link>
      );
    }
    return <React.Fragment key={idx}>{p.v}</React.Fragment>;
  });
}

function renderFormatted(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  let i = 0;
  const pushPara = (node) => out.push(node);
  while (i < lines.length) {
    const raw = String(lines[i] || '');
    const line = raw;

    // blank line
    if (!line.trim()) {
      out.push(<Box key={`sp-${i}`} h={2} />);
      i += 1;
      continue;
    }

    // youtube embed token
    const ym = line.trim().match(/^\{\{youtube:([a-zA-Z0-9_-]{11})\}\}$/);
    if (ym?.[1]) {
      const id = ym[1];
      out.push(
        <Box key={`yt-${i}`} rounded="xl" overflow="hidden" borderWidth="1px" borderColor="gray.100">
          <AspectRatio ratio={16 / 9}>
            <iframe
              title="YouTube"
              src={`https://www.youtube-nocookie.com/embed/${id}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </AspectRatio>
        </Box>,
      );
      i += 1;
      continue;
    }

    // markdown image line: ![alt](url)
    const im = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (im?.[2]) {
      out.push(
        <Box key={`img-${i}`} rounded="xl" overflow="hidden" borderWidth="1px" borderColor="gray.100" bg="gray.50">
          <Image src={im[2]} alt={im[1] || 'Imagen'} w="full" maxH="60vh" objectFit="contain" />
        </Box>,
      );
      i += 1;
      continue;
    }

    // unordered list block
    if (line.trim().startsWith('- ')) {
      const items = [];
      while (i < lines.length && String(lines[i] || '').trim().startsWith('- ')) {
        items.push(String(lines[i]).trim().slice(2));
        i += 1;
      }
      out.push(
        <List.Root as='ul' key={`ul-${i}`} gap={1} pl={5}>
          {items.map((t, idx) => (
            <List.Item key={idx} color="gray.700" lineHeight="1.9">
              {renderInline(t)}
            </List.Item>
          ))}
        </List.Root>,
      );
      continue;
    }

    // ordered list block: "1. "
    if (/^\s*\d+\.\s+/.test(line.trim())) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(String(lines[i] || '').trim())) {
        items.push(String(lines[i]).trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      out.push(
        <List.Root as='ol' key={`ol-${i}`} gap={1} pl={5}>
          {items.map((t, idx) => (
            <List.Item key={idx} color="gray.700" lineHeight="1.9">
              {renderInline(t)}
            </List.Item>
          ))}
        </List.Root>,
      );
      continue;
    }

    // headings
    if (line.startsWith('# ')) {
      out.push(
        <Text key={`h1-${i}`} fontSize={{ base: 'lg', md: 'xl' }} fontWeight="900" lineHeight="1.4">
          {renderInline(line.slice(2))}
        </Text>,
      );
      i += 1;
      continue;
    }
    if (line.startsWith('## ')) {
      out.push(
        <Text key={`h2-${i}`} fontSize={{ base: 'md', md: 'lg' }} fontWeight="800" lineHeight="1.5">
          {renderInline(line.slice(3))}
        </Text>,
      );
      i += 1;
      continue;
    }

    // paragraph
    pushPara(
      <Text key={`p-${i}`} fontSize="md" lineHeight="1.9" color="gray.700" whiteSpace="pre-wrap">
        {renderInline(line)}
      </Text>,
    );
    i += 1;
  }

  return (
    <VStack align="stretch" gap={2}>
      {out}
    </VStack>
  );
}

function extractTitleFromTexto(texto) {
  const lines = String(texto || '').split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return 'Noticia';
  const first = lines[0];
  const m = first.match(/^\s*(#{1,2})\s+(.*)$/);
  if (m?.[2]) return m[2].trim() || 'Noticia';
  return first.length > 80 ? `${first.slice(0, 80)}…` : first;
}

function stripTitleLineFromTexto(texto) {
  const lines = String(texto || '').split('\n');
  if (lines.length === 0) return '';
  const first = (lines[0] || '').trim();
  if (/^\s*#{1,2}\s+/.test(first)) return lines.slice(1).join('\n').trim();
  return String(texto || '').trim();
}

function extractHeadingAndFirstParagraph(texto) {
  const lines = String(texto || '').split('\n');
  const headingLine = (lines[1] || '').trim();
  const heading = headingLine.replace(/^\s*##\s+/, '').trim();
  const bodyLines = lines.slice(2);
  const firstPara = bodyLines
    .map((l) => String(l || '').trim())
    .filter((l) => l && !/^\s*(!\[|\{\{youtube:|- |\d+\.\s+)/.test(l))[0] || '';
  return { heading, firstPara };
}

export default function MicrositeNoticiasBlock({
  msConfig,
  primaryColor = '#7c3aed',
  variant = 'default',
}) {
  const items = Array.isArray(msConfig?.noticias) ? msConfig.noticias : [];
  const active = useMemo(
    () =>
      items
        .filter((n) => n && n.activo === true && String(n.texto || '').trim())
        .slice(0, 10),
    [items],
  );

  const [selected, setSelected] = useState(null);

  if (active.length === 0) {
    return (
      <Text fontSize={{ base: 'md', md: 'lg' }} lineHeight="1.8" color="gray.500" textAlign="center">
        {MICROSITE_SECTION_EMPTY_HINT.noticias}
      </Text>
    );
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4} justifyItems="center">
        {active.map((n) => {
          const date = n.createdAt ? new Date(n.createdAt) : null;
          const dateLabel = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '';
          const title = extractTitleFromTexto(n.texto);
          const { heading, firstPara } = extractHeadingAndFirstParagraph(n.texto);
          const headingPreview = heading.length > 120 ? `${heading.slice(0, 120)}…` : heading;
          const paraPreview = firstPara.length > 160 ? `${firstPara.slice(0, 160)}…` : firstPara;

          return (
            <Box
              key={n.id || `${n.createdAt || ''}-${n.texto?.slice(0, 20)}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(n)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelected(n);
              }}
              w="full"
              maxW={{ base: '560px', sm: 'unset' }}
              borderWidth="1px"
              borderColor={variant === 'glass' ? 'whiteAlpha.300' : 'gray.200'}
              bg={variant === 'glass' ? 'whiteAlpha.700' : 'white'}
              rounded="2xl"
              overflow="hidden"
              shadow="sm"
              _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
              transition="all 0.15s ease"
            >
              <AspectRatio ratio={16 / 9} bg={variant === 'glass' ? 'whiteAlpha.200' : 'gray.100'}>
                {n.imagen ? (
                  <Image src={n.imagen} alt={title} objectFit="cover" />
                ) : (
                  <Box
                    w="full"
                    h="full"
                    bgGradient={`linear(to-br, ${primaryColor}22, transparent)`}
                  />
                )}
              </AspectRatio>
              <VStack align="stretch" gap={2} p={4}>
                <HStack justify="space-between" align="baseline">
                  <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="wide">
                    Noticia
                  </Text>
                  {!!dateLabel && (
                    <Text fontSize="xs" color="gray.500">
                      {dateLabel}
                    </Text>
                  )}
                </HStack>
                <Text fontWeight="900" fontSize="md" lineHeight="1.4" color="gray.800" lineClamp={2}>
                  {title}
                </Text>
                {!!headingPreview && (
                  <Text fontSize="sm" color="gray.700" fontWeight="700" lineClamp={2} whiteSpace="pre-line">
                    {renderInline(headingPreview)}
                  </Text>
                )}
                {!!paraPreview && (
                  <Text fontSize="sm" color="gray.600" lineClamp={3} whiteSpace="pre-line">
                    {renderInline(paraPreview)}
                  </Text>
                )}
              </VStack>
            </Box>
          );
        })}
      </SimpleGrid>

      <Dialog.Root
        open={!!selected}
        size={{ base: 'xl', md: '3xl', lg: '5xl' }}
        placement='center'
        onOpenChange={e => {
          if (!e.open) {
            setSelected(null);
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content rounded="2xl" overflow="hidden">
              <Dialog.Header pt={6} pr={14}>
                <HStack justify="space-between" align="baseline" gap={3}>
                  <Text fontWeight="900" fontSize="lg" lineClamp={2}>
                    {selected ? extractTitleFromTexto(selected.texto) : 'Noticia'}
                  </Text>
                  {selected?.createdAt ? (
                    <Text fontSize="sm" color="gray.500" flexShrink={0}>
                      {(() => {
                        const d = new Date(selected.createdAt);
                        return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
                      })()}
                    </Text>
                  ) : null}
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body
                pb={6}
                maxH={{ base: '80vh', md: '78vh' }}
                overflowY="auto"
                pr={4}
                css={{
                  scrollbarGutter: 'stable',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#a0aec0 transparent',
                  '& &::-webkit-scrollbar': { width: '10px' },
                  '& &::-webkit-scrollbar-track': { background: 'transparent' },

                  '& &::-webkit-scrollbar-thumb': {
                    background: 'rgba(160, 174, 192, 0.75)',
                    borderRadius: '999px',
                    border: '3px solid transparent',
                    backgroundClip: 'content-box',
                  },

                  '& &::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(113, 128, 150, 0.85)',
                    backgroundClip: 'content-box',
                  }
                }}
              >
                {selected?.imagen ? (
                  <Box
                    mb={4}
                    rounded="xl"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.100"
                    bg="gray.50"
                  >
                    <Image
                      src={selected.imagen}
                      alt="Imagen noticia"
                      w="full"
                      maxH="60vh"
                      objectFit="contain"
                    />
                  </Box>
                ) : null}
                {selected ? renderFormatted(String(selected.texto || '').trim()) : null}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
}

